import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

const TableManagement = ({ apiClient }) => {
  const [tables, setTables] = useState([]);
  const [qrCodes, setQrCodes] = useState({}); // Store dynamic QR codes
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    table_number: '',
    qr_source: '',
    capacity: 4
  });

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/restaurant/tables');
      setTables(response.data);
      // Generate QR codes for all tables dynamically
      await generateQRCodes(response.data);
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error('Failed to load tables');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCodes = async (tableList) => {
    const newQrCodes = {};
    
    for (const table of tableList) {
      try {
        // Generate QR code using current domain (like Copy Menu Link)
        const menuUrl = `${window.location.origin}/menu/${table.restaurant_id}/${table.id}`;
        
        // Call the dynamic QR generation endpoint
        const response = await apiClient.get(`/qr-code?restaurant_id=${table.restaurant_id}&table_id=${table.id}`);
        newQrCodes[table.id] = {
          qr_code: response.data.qr_code,
          menu_url: menuUrl
        };
      } catch (error) {
        console.error(`Error generating QR code for table ${table.id}:`, error);
        // Fallback - generate a simple placeholder
        newQrCodes[table.id] = {
          qr_code: null,
          menu_url: `${window.location.origin}/menu/${table.restaurant_id}/${table.id}`
        };
      }
    }
    
    setQrCodes(newQrCodes);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle table number with character limit validation
    if (name === 'table_number') {
      const limitedValue = value.slice(0, 6);
      
      // Show toast notifications for character limits
      if (value.length > 6) {
        toast.warning(`Character limit reached! Table number limited to 6 characters.`, {
          duration: 3000,
          position: 'top-center'
        });
      } else if (value.length >= 5 && value.length <= 6) {
        // Warning when approaching limit
        toast.info(`${6 - value.length} characters remaining for table number`, {
          duration: 1500,
          position: 'top-center'
        });
      }
      
      setFormData({
        ...formData,
        [name]: limitedValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: name === 'capacity' ? parseInt(value) || 4 : value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiClient.post('/restaurant/tables', formData);
      toast.success('Table created successfully!');
      resetForm();
      fetchTables();
    } catch (error) {
      console.error('Error creating table:', error);
      toast.error(error.response?.data?.detail || 'Failed to create table');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tableId) => {
    if (!confirm('Are you sure you want to delete this table? This will also delete its QR code.')) return;

    try {
      await apiClient.delete(`/restaurant/tables/${tableId}`);
      toast.success('Table deleted successfully!');
      fetchTables();
    } catch (error) {
      console.error('Error deleting table:', error);
      toast.error('Failed to delete table');
    }
  };

  const resetForm = () => {
    setFormData({
      table_number: '',
      qr_source: '',
      capacity: 4
    });
    setShowForm(false);
  };

  const downloadQR = (table) => {
    const qrData = qrCodes[table.id];
    if (!qrData?.qr_code) {
      toast.error('QR code not available for this table');
      return;
    }
    
    // Open the same formatted view as print but for download viewing
    const downloadWindow = window.open('', '_blank');
    downloadWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - Table ${table.table_number}</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: Arial, sans-serif; 
              text-align: center; 
              background: white;
            }
            .qr-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 90vh;
            }
            .qr-code {
              margin: 30px 0;
            }
            .table-info {
              margin-bottom: 30px;
              font-size: 32px;
              font-weight: bold;
              color: #333;
            }
            .scan-instruction {
              margin-top: 30px;
              font-size: 28px;
              font-weight: bold;
              color: #2563eb;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .download-info {
              position: fixed;
              top: 10px;
              right: 10px;
              background: #f3f4f6;
              padding: 10px;
              border-radius: 8px;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 0; padding: 15px; }
              .qr-container { min-height: auto; }
              .download-info { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="download-info">
            Right-click and "Save As" to download<br>
            Or use Ctrl+P to print
          </div>
          <div class="qr-container">
            <div class="table-info">Table ${table.table_number}</div>
            <div class="qr-code">
              <img src="${qrData.qr_code}" alt="QR Code" style="width: 300px; height: 300px;">
            </div>
            <div class="scan-instruction">
              Scan to Place Order
            </div>
          </div>
        </body>
      </html>
    `);
    downloadWindow.document.close();
  };

  const printQR = (table) => {
    const qrData = qrCodes[table.id];
    if (!qrData?.qr_code) {
      toast.error('QR code not available for this table');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - Table ${table.table_number}</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: Arial, sans-serif; 
              text-align: center; 
              background: white;
            }
            .qr-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 90vh;
            }
            .qr-code {
              margin: 30px 0;
            }
            .table-info {
              margin-bottom: 30px;
              font-size: 32px;
              font-weight: bold;
              color: #333;
            }
            .scan-instruction {
              margin-top: 30px;
              font-size: 28px;
              font-weight: bold;
              color: #2563eb;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            @media print {
              body { margin: 0; padding: 15px; }
              .qr-container { min-height: auto; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="table-info">Table ${table.table_number}</div>
            <div class="qr-code">
              <img src="${qrData.qr_code}" alt="QR Code" style="width: 300px; height: 300px;">
            </div>
            <div class="scan-instruction">
              Scan to Place Order
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const copyMenuLink = async (table) => {
    const menuUrl = `${window.location.origin}/menu/${table.restaurant_id}/${table.id}`;
    try {
      await navigator.clipboard.writeText(menuUrl);
      toast.success('Menu link copied to clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = menuUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Menu link copied to clipboard!');
    }
  };

  // Regenerate function removed - no longer needed with fixed URL generation

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block">
              <div className="spinner w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600 mt-3">Loading tables and QR codes...</p>
          </div>
        </div>
      )}

      {!loading && (
        <>
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Tables & QR Codes</h2>
            <div className="flex gap-3">
              <button
                onClick={() => setShowForm(true)}
                data-testid="add-table-button"
                className="btn-primary"
              >
                Add New Table
              </button>
            </div>
          </div>
        </div>

        {/* Table Form */}
        {showForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium mb-4">Add New Table</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="table_number" className="form-label">
                    Table Number *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="table_number"
                      name="table_number"
                      value={formData.table_number}
                      onChange={handleInputChange}
                      data-testid="table-number-input"
                      className="form-input pr-12"
                      placeholder="e.g., 1, A1, T5"
                      maxLength={6}
                      required
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                      {formData.table_number.length}/6
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="qr_source" className="form-label">
                    Table Label/Description (Optional)
                  </label>
                  <input
                    type="text"
                    id="qr_source"
                    name="qr_source"
                    value={formData.qr_source}
                    onChange={handleInputChange}
                    data-testid="table-source-input"
                    className="form-input"
                    placeholder="e.g., Table 1, Window Seat A1"
                  />
                </div>
                <div>
                  <label htmlFor="capacity" className="form-label">
                    Capacity (Seats)
                  </label>
                  <input
                    type="number"
                    id="capacity"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    data-testid="table-capacity-input"
                    className="form-input"
                    placeholder="4"
                    min="1"
                    max="20"
                  />
                </div>
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="save-table-button"
                  className="btn-primary"
                >
                  {loading ? <div className="spinner"></div> : 'Create Table & QR Code'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="cancel-table-button"
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tables Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tables.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500" data-testid="no-tables-message">
              No tables created yet. Click "Add Table" to get started.
            </div>
          ) : (
            tables.map((table) => (
              <div
                key={table.id}
                className="qr-code-container"
                data-testid={`table-item-${table.id}`}
              >
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900" data-testid={`table-name-${table.id}`}>
                    {table.table_number}
                  </h3>
                  <p className="text-sm text-gray-600" data-testid={`table-source-${table.id}`}>
                    {table.qr_source}
                  </p>
                  <p className="text-xs text-gray-500">
                    Capacity: {table.capacity} seats
                  </p>
                </div>

                <div className="qr-code mb-4">
                  {qrCodes[table.id]?.qr_code ? (
                    <img
                      src={qrCodes[table.id].qr_code}
                      alt={`QR Code for ${table.table_number}`}
                      className="w-48 h-48 mx-auto border border-gray-200 rounded"
                      data-testid={`table-qr-${table.id}`}
                    />
                  ) : (
                    <div className="w-48 h-48 mx-auto border border-gray-300 rounded bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-500">Generating QR Code...</span>
                    </div>
                  )}
                </div>

                {/* QR Code URL Display */}
                {qrCodes[table.id]?.menu_url && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs font-medium text-blue-900 mb-1">QR Code URL:</p>
                    <p className="text-xs text-blue-700 break-all">{qrCodes[table.id].menu_url}</p>
                    <p className="text-xs text-blue-600 mt-1">
                      📱 Customers scan QR to access this URL
                    </p>
                  </div>
                )}

                <div className="space-y-2 w-full">
                  <button
                    onClick={() => downloadQR(table)}
                    data-testid={`download-qr-${table.id}`}
                    className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Download QR Code
                  </button>
                  <button
                    onClick={() => printQR(table)}
                    data-testid={`print-qr-${table.id}`}
                    className="w-full px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Print QR Code
                  </button>
                  <button
                    onClick={() => copyMenuLink(table)}
                    data-testid={`copy-link-${table.id}`}
                    className="w-full px-3 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    Copy Menu Link
                  </button>
                  <button
                    onClick={() => handleDelete(table.id)}
                    data-testid={`delete-table-${table.id}`}
                    className="w-full px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Delete Table
                  </button>
                </div>

                <div className="mt-2 text-xs text-gray-400">
                  Created: {new Date(table.created_at).toLocaleDateString()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default TableManagement;